import { eventChannel } from 'redux-saga';
import { all, call, cancel, cancelled, fork, put, race, take, takeLatest } from 'redux-saga/effects';
import { collection, doc, getDoc, getDocs, onSnapshot, query, where } from 'firebase/firestore';

import { db } from 'config/firebase';
import { GET_RENTALS, GET_RENTAL_PROPERTIES, LOGOUT_USER, SET_ACTIVE_USER_ORG } from '../actions/types';
import {
    getRentalsSuccess,
    getRentalsFailure,
    getRentalPropertiesSuccess,
    getRentalPropertiesFailure
} from '../actions/Rentals';

const rentalsCollectionRef = collection(db, 'rentals');

// One getDoc per unique id (same single-doc-read pattern Properties.js already uses),
// run in parallel. Returns a { [id]: data } map — ids with no matching doc are simply
// absent, never a crash.
function* fetchDocsById(collectionName, ids) {
    const uniqueIds = [...new Set(ids.filter(Boolean))];
    if (!uniqueIds.length) return {};

    const snaps = yield all(uniqueIds.map(id => call(getDoc, doc(db, collectionName, id))));

    const byId = {};
    snaps.forEach((snap, index) => {
        if (snap.exists()) {
            byId[uniqueIds[index]] = snap.data();
        }
    });
    return byId;
}

// Mirrors rentalStatusRules on the backend (sage-nest-api). The status field only
// actually flips in Firestore when the `rentals-cron` sweep (update-rental-statuses)
// runs, so a rental can sit stored as "upcoming" for a while after its start_date has
// already passed. Recomputed here so the table and the Status filter always reflect
// "now" instead of the last sweep.
const deriveStatus = rental => {
    const now = new Date();
    const start =
        typeof rental.start_date?.seconds === 'number'
            ? new Date(rental.start_date.seconds * 1000)
            : null;
    const end =
        typeof rental.end_date?.seconds === 'number'
            ? new Date(rental.end_date.seconds * 1000)
            : null;

    if (rental.status === 'upcoming' && start && now >= start) return 'active';
    if (rental.status === 'active' && end && now > end) return 'expired';
    return rental.status;
};

// Rental docs only store ids (property_id / renter_id / renter_invite_id) — the backend
// normally resolves these server-side (RentalService.getRentalsByOrgId's queryWithJoin).
// Replicated here so the live listener can render the same Name/Property columns without
// going through the API. `property` always comes back as an object (never null) since
// RentalsTable reads `property.full_address` unguarded — a missing property doc just
// means every field on it is null, not a crash.
function* joinRentals(rawRentals) {
    const [propertiesById, usersById, invitesById] = yield all([
        call(fetchDocsById, 'properties', rawRentals.map(r => r.property_id)),
        call(fetchDocsById, 'users', rawRentals.map(r => r.renter_id)),
        call(fetchDocsById, 'invites', rawRentals.map(r => r.renter_invite_id))
    ]);

    return rawRentals.map(rental => {
        const property = propertiesById[rental.property_id];
        const renterUser = rental.renter_id ? usersById[rental.renter_id] : null;
        const renterInvite = rental.renter_invite_id
            ? invitesById[rental.renter_invite_id]
            : null;

        return {
            ...rental,
            status: deriveStatus(rental),
            property: {
                address_1: property?.address_1 ?? null,
                address_2: property?.address_2 ?? null,
                city: property?.city ?? null,
                state: property?.state ?? null,
                full_address: property?.full_address ?? null
            },
            renter_user: renterUser
                ? {
                      first_name: renterUser.first_name,
                      last_name: renterUser.last_name,
                      phone: renterUser.phone,
                      email: renterUser.email
                  }
                : null,
            renter_invite: renterInvite
                ? {
                      first_name: renterInvite.first_name,
                      last_name: renterInvite.last_name,
                      phone: renterInvite.phone,
                      email: renterInvite.email
                  }
                : null
        };
    });
}

// Single live subscription per org — org_id is the only server-side query constraint.
// Property/status/date/search filtering happens client-side via filterRentals (see
// reducers/Rentals.js), so Apply/Reset/search never need to tear down and re-subscribe.
// managedPropertyIds is applied here, before the property/renter joins, so a scoped
// rental manager's excluded rentals never even get looked up — same restriction
// RentalService.getRentalsByOrgId applies server-side.
function* rentalsCollectionWatch(orgId, managedPropertyIds) {
    let unsubscribe;

    const channel = eventChannel(emit => {
        const rentalsQuery = query(rentalsCollectionRef, where('org_id', '==', orgId));
        unsubscribe = onSnapshot(
            rentalsQuery,
            snap => emit({ docs: snap.docs.map(docSnap => docSnap.data()) }),
            error => emit({ error })
        );
        return () => unsubscribe();
    });

    try {
        while (true) {
            const { userSignOut, resettingOrg, data } = yield race({
                userSignOut: take(LOGOUT_USER),
                resettingOrg: take(SET_ACTIVE_USER_ORG),
                data: take(channel)
            });

            if (userSignOut || resettingOrg) {
                channel.close();
                return;
            }

            if (data?.error) {
                yield put(
                    getRentalsFailure(data.error?.message || 'Failed to load reservations')
                );
                continue;
            }

            const scopedDocs = managedPropertyIds?.length
                ? data.docs.filter(rental => managedPropertyIds.includes(rental.property_id))
                : data.docs;

            try {
                const joined = yield call(joinRentals, scopedDocs);
                yield put(getRentalsSuccess(joined));
            } catch (error) {
                yield put(getRentalsFailure(error?.message || 'Failed to load reservations'));
            }
        }
    } finally {
        if (unsubscribe) unsubscribe();
        if (yield cancelled()) {
            channel.close();
        }
    }
}

let liveTask = null;

function* startRentalsLive({ payload }) {
    const { orgId, managedPropertyIds } = payload;
    if (!orgId) return;

    if (liveTask) {
        yield cancel(liveTask);
        liveTask = null;
    }

    liveTask = yield fork(rentalsCollectionWatch, orgId, managedPropertyIds);
}

export function* watchGetRentals() {
    yield takeLatest(GET_RENTALS, startRentalsLive);
}

// One-shot (not live) read for the "Property address" filter dropdown — vacant
// rental-type properties for the org, mirroring RentalService.getRentalOrgProperties.
// managedPropertyIds narrows it to a scoped rental manager's assigned properties.
function* fetchRentalProperties({ payload }) {
    const { orgId, managedPropertyIds } = payload;
    if (!orgId) return;

    try {
        const propertiesQuery = query(
            collection(db, 'properties'),
            where('org_id', '==', orgId),
            where('type', '==', 'rental'),
            where('rental_start', '==', null),
            where('rental_end', '==', null)
        );
        const snap = yield call(getDocs, propertiesQuery);

        const options = snap.docs
            .map(docSnap => docSnap.data())
            .filter(
                property =>
                    !managedPropertyIds?.length || managedPropertyIds.includes(property.id)
            )
            .map(property => ({
                value: property.id,
                label: property.full_address || property.address_1 || property.id
            }));

        yield put(getRentalPropertiesSuccess(options));
    } catch (error) {
        yield put(getRentalPropertiesFailure(error?.message || 'Failed to load properties'));
    }
}

export function* watchGetRentalProperties() {
    yield takeLatest(GET_RENTAL_PROPERTIES, fetchRentalProperties);
}

export default function* rootSaga() {
    yield all([fork(watchGetRentals), fork(watchGetRentalProperties)]);
}
